import { useMemo, useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { format, parseISO } from "date-fns";
import { Loader2, Pencil, Trash2, X } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { trackPageView } from "@/lib/analytics";
import {
  settingsKeys,
  useCreateImportantDate,
  useDeleteImportantDate,
  useEditImportantDate,
  useImportantDateCategories,
  useImportantDates
} from "@/services/settings";
import { cn } from "@/utils/cn";
import type { ImportantDate } from "@/types/settings";

const schema = z
  .object({
    title: z.string().min(1, "Title is required"),
    categoryName: z.string().min(1, "Category is required"),
    begin: z.string().min(1, "Begin date is required"),
    end: z.string().min(1, "End date is required"),
    doNotBook: z.boolean()
  })
  .refine((value) => value.begin <= value.end, {
    path: ["end"],
    message: "End date must be on or after begin date"
  });

type FormValues = z.infer<typeof schema>;

type SortKey = "categoryName" | "title" | "doNotBook" | "begin" | "end";
type SortDirection = "asc" | "desc";
type SortState = Record<SortKey, SortDirection | null>;

const SORT_COLUMN_ORDER: SortKey[] = ["categoryName", "title", "doNotBook", "begin", "end"];

function SortableHeader({
  label,
  sortDirection,
  onClick
}: Readonly<{
  label: string;
  sortDirection: SortDirection | null;
  onClick: () => void;
}>) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full inline-flex items-center gap-1 px-1 py-1 hover:bg-slate-300 transition-colors cursor-pointer select-none"
      title={`Click to sort by ${label}. Current: ${sortDirection ? sortDirection.toUpperCase() : "None"}`}
    >
      <span>{label}</span>
      {sortDirection ? (
        <span className="text-xs font-bold" aria-label={`Sorted ${sortDirection}`}>
          {sortDirection === "asc" ? "↑" : "↓"}
        </span>
      ) : (
        <span className="text-xs text-slate-400 opacity-50">⇅</span>
      )}
    </button>
  );
}

function toApiDate(date: string) {
  return `${date}T00:00:00`;
}

function displayDate(value: string | null) {
  if (!value) return "-";
  try {
    return format(parseISO(value), "MM-dd-yyyy");
  } catch {
    return "-";
  }
}

export function ImportantDatesPage() {
  const currentUser = useCurrentUser();
  const userId = currentUser?.email ?? "Unknown";

  // Track page view on mount
  useEffect(() => {
    trackPageView("Important Dates", userId);
  }, [userId]);

  const [editing, setEditing] = useState<ImportantDate | null>(null);
  const [sortState, setSortState] = useState<SortState>({
    categoryName: null,
    title: null,
    doNotBook: null,
    begin: null,
    end: null
  });
  const queryClient = useQueryClient();
  const { data, isPending, isFetching } = useImportantDates();
  const { data: categories = [] } = useImportantDateCategories();
  const createMutation = useCreateImportantDate();
  const editMutation = useEditImportantDate();
  const deleteMutation = useDeleteImportantDate();

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      title: "",
      categoryName: "",
      begin: "",
      end: "",
      doNotBook: false
    }
  });

  const beginField = form.register("begin");

  const rows = useMemo(() => data ?? [], [data]);

  const activeSorts = useMemo(() => {
    return SORT_COLUMN_ORDER.flatMap((col) => {
      const dir = sortState[col];
      return dir === null ? [] : [{ key: col, direction: dir }];
    });
  }, [sortState]);

  const sortedRows = useMemo(() => {
    if (activeSorts.length === 0) return rows;

    return [...rows].sort((a, b) => {
      for (const sort of activeSorts) {
        let aValue: string | number;
        let bValue: string | number;

        switch (sort.key) {
          case "doNotBook":
            aValue = a.doNotBook ? 1 : 0;
            bValue = b.doNotBook ? 1 : 0;
            break;
          case "begin":
          case "end": {
            const aDate = a[sort.key];
            const bDate = b[sort.key];
            aValue = aDate ? parseISO(aDate).getTime() : Number.NEGATIVE_INFINITY;
            bValue = bDate ? parseISO(bDate).getTime() : Number.NEGATIVE_INFINITY;
            break;
          }
          default:
            aValue = (a[sort.key] ?? "").toString().toLowerCase();
            bValue = (b[sort.key] ?? "").toString().toLowerCase();
        }

        if (aValue < bValue) return sort.direction === "asc" ? -1 : 1;
        if (aValue > bValue) return sort.direction === "asc" ? 1 : -1;
      }
      return 0;
    });
  }, [rows, activeSorts]);

  const handleColumnSort = (key: SortKey) => {
    setSortState((prev) => {
      const current = prev[key];
      let next: SortDirection | null;
      if (current === null) { next = "asc"; }
      else if (current === "asc") { next = "desc"; }
      else { next = null; }
      return { ...prev, [key]: next };
    });
  };

  const getAriaSort = (key: SortKey): "none" | "ascending" | "descending" => {
    const dir = sortState[key];
    if (!dir) return "none";
    return dir === "asc" ? "ascending" : "descending";
  };

  const closeModal = () => {
    setEditing(null);
    form.reset({
      title: "",
      categoryName: "",
      begin: "",
      end: "",
      doNotBook: false
    });
  };

  const openCreate = () => {
    setEditing({
      id: 0,
      title: "",
      begin: null,
      end: null,
      recurring: "",
      doNotBook: false,
      dayOfWeek: "",
      categoryId: 0,
      categoryName: "",
      categoryCssClass: "",
      categoryHeaderCssClass: "",
      orderOfCategory: 0
    });
    form.reset({
      title: "",
      categoryName: "",
      begin: "",
      end: "",
      doNotBook: false
    });
  };

  const openEdit = (item: ImportantDate) => {
    setEditing(item);
    form.reset({
      title: item.title,
      categoryName: item.categoryName,
      begin: item.begin ? format(parseISO(item.begin), "yyyy-MM-dd") : "",
      end: item.end ? format(parseISO(item.end), "yyyy-MM-dd") : "",
      doNotBook: Boolean(item.doNotBook)
    });
  };

  const onSubmit = form.handleSubmit(async (values) => {
    const category = categories.find((c) => c.categoryName === values.categoryName);
    if (!category) {
      form.setError("categoryName", { message: "Invalid category" });
      return;
    }

    try {
      if (editing?.id && editing.id > 0) {
        await editMutation.mutateAsync({
          importantDateId: editing.id,
          title: values.title,
          categoryId: category.categoryId,
          doNotBook: values.doNotBook,
          begin: toApiDate(values.begin),
          end: toApiDate(values.end),
          recurring: "",
          dayOfWeek: ""
        });
        toast.success("Important date updated successfully.");
      } else {
        await createMutation.mutateAsync({
          title: values.title,
          categoryId: category.categoryId,
          doNotBook: values.doNotBook,
          begin: toApiDate(values.begin),
          end: toApiDate(values.end),
          recurring: "",
          dayOfWeek: ""
        });
        toast.success("Important date created successfully.");
      }

      await queryClient.invalidateQueries({ queryKey: settingsKeys.importantDates() });
      closeModal();
    } catch (error) {
      toast.error("Unable to save important date.");
    }
  });

  const onDelete = async (item: ImportantDate) => {
    const confirmed = window.confirm(`Delete important date \"${item.title}\"?`);
    if (!confirmed) return;

    try {
      await deleteMutation.mutateAsync({ importantDateId: item.id });
      toast.success("Important date deleted successfully.");
      await queryClient.invalidateQueries({ queryKey: settingsKeys.importantDates() });
    } catch {
      toast.error("Unable to delete important date.");
    }
  };

  return (
    <section className="rounded border-slate-200 bg-white">
      <div className="mb-4 flex items-center justify-end">
        <button
          type="button"
          onClick={openCreate}
          className="px-3 py-2 text-sm font-medium text-white bg-[#26890d] hover:bg-[#1f6609]"
        >
          Add date
        </button>
      </div>

      {isPending ? (
        <div className="py-8 text-sm text-slate-600">Loading important dates...</div>
      ) : (
        <div className="overflow-x-auto overflow-y-auto" style={{ height: "calc(90vh - 150px)" }}>
          <table className="min-w-full border border-slate-200 text-left text-sm">
            <thead className="sticky top-0 bg-[#E8E8E8] text-xs font-semibold text-black">
              <tr>
                <th className="border-b border-slate-200 p-0" aria-sort={getAriaSort("categoryName")}>
                  <SortableHeader label="Category" sortDirection={sortState.categoryName} onClick={() => handleColumnSort("categoryName")} />
                </th>
                <th className="border-b border-slate-200 p-0" aria-sort={getAriaSort("title")}>
                  <SortableHeader label="Title" sortDirection={sortState.title} onClick={() => handleColumnSort("title")} />
                </th>
                <th className="border-b border-slate-200 p-0 text-center" aria-sort={getAriaSort("doNotBook")}>
                  <SortableHeader label="Do Not Book" sortDirection={sortState.doNotBook} onClick={() => handleColumnSort("doNotBook")} />
                </th>
                <th className="border-b border-slate-200 p-0" aria-sort={getAriaSort("begin")}>
                  <SortableHeader label="Begin" sortDirection={sortState.begin} onClick={() => handleColumnSort("begin")} />
                </th>
                <th className="border-b border-slate-200 p-0" aria-sort={getAriaSort("end")}>
                  <SortableHeader label="End" sortDirection={sortState.end} onClick={() => handleColumnSort("end")} />
                </th>
                <th className="border-b border-slate-200 px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((item, idx) => (
                <tr key={item.id} className={idx % 2 === 0 ? "bg-white" : "bg-[#F2F2F2]"}>
                  <td className="border-b border-slate-200 px-3 py-2">{item.categoryName}</td>
                  <td className="border-b border-slate-200 px-3 py-2">{item.title}</td>
                  <td className="border-b border-slate-200 px-3 py-2 text-center">
                    {item.doNotBook ? (
                      <span className="inline-flex h-4 w-4 items-center justify-center rounded-full text-xs text-white" style={{backgroundColor: "rgb(236, 144, 137)"}}></span>
                    ) : (
                      <span className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-slate-300 text-xs"></span>
                    )}
                  </td>
                  <td className="border-b border-slate-200 px-3 py-2">{displayDate(item.begin)}</td>
                  <td className="border-b border-slate-200 px-3 py-2">{displayDate(item.end)}</td>
                  <td className="border-b border-slate-200 px-3 py-2">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => openEdit(item)}
                        className="inline-flex h-8 w-8 items-center justify-center border border-slate-300 text-slate-700 hover:bg-slate-50"
                        aria-label={`Edit ${item.title}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => onDelete(item)}
                        className="inline-flex h-8 w-8 items-center justify-center border border-slate-300 text-slate-700 hover:bg-slate-50"
                        aria-label={`Delete ${item.title}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {sortedRows.length === 0 ? (
                <tr>
                  <td className="px-3 py-8 text-center text-slate-500" colSpan={6}>
                    No important dates available.
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      )}

      {isFetching && !isPending ? (
        <div className="mt-2 text-xs text-slate-500">Refreshing important dates...</div>
      ) : null}

      {editing ? (
        <div 
          className="fixed inset-0 z-50 flex bg-black/30" 
          role="dialog" 
          aria-modal="true" 
          aria-labelledby="important-date-modal-title"
          onClick={closeModal}
        >
          <div 
            className="ml-auto flex w-96 flex-col border-l border-slate-300 bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between bg-black p-4">
              <h3 id="important-date-modal-title" className="text-base font-semibold text-white">
                {editing.id > 0 ? "Edit Date" : "Add Date"}
              </h3>
              <button type="button" onClick={closeModal} className="text-white hover:text-slate-300" aria-label="Close dialog">
                <X className="h-4 w-4" />
              </button>
            </div>

            <form className="flex-1 space-y-3 overflow-y-auto p-4" id="important-date-form" onSubmit={onSubmit}>
              <div>
                <label htmlFor="important-date-category" className="mb-1 block text-sm font-medium text-slate-800">
                  Category
                </label>
                <select
                  id="important-date-category"
                  className={cn("w-full border border-slate-300 px-3 py-2 text-sm", form.formState.errors.categoryName && "border-red-500")}
                  {...form.register("categoryName")}
                >
                  <option value="">Select category</option>
                  {categories.map((category) => (
                    <option key={category.categoryId} value={category.categoryName}>
                      {category.categoryName}
                    </option>
                  ))}
                </select>
                {form.formState.errors.categoryName ? (
                  <p className="mt-1 text-xs text-red-600">{form.formState.errors.categoryName.message}</p>
                ) : null}
              </div>

              <div>
                <label htmlFor="important-date-title" className="mb-1 block text-sm font-medium text-slate-800">
                  Title
                </label>
                <input
                  id="important-date-title"
                  type="text"
                  className={cn("w-full border border-slate-300 px-3 py-2 text-sm", form.formState.errors.title && "border-red-500")}
                  {...form.register("title")}
                />
                {form.formState.errors.title ? (
                  <p className="mt-1 text-xs text-red-600">{form.formState.errors.title.message}</p>
                ) : null}
              </div>

              <div className="grid gap-3">
                <div>
                  <label htmlFor="important-date-begin" className="mb-1 block text-sm font-medium text-slate-800">
                    Begin
                  </label>
                  <input
                    id="important-date-begin"
                    type="date"
                    className={cn("w-full border border-slate-300 px-3 py-2 text-sm", form.formState.errors.begin && "border-red-500")}
                    {...beginField}
                    onChange={(event) => {
                      beginField.onChange(event);
                      const beginValue = event.target.value;

                      if (!beginValue) {
                        form.setValue("end", "", { shouldDirty: true, shouldValidate: true });
                        return;
                      }

                      form.setValue("end", beginValue, { shouldDirty: true, shouldValidate: true });
                    }}
                  />
                  {form.formState.errors.begin ? (
                    <p className="mt-1 text-xs text-red-600">{form.formState.errors.begin.message}</p>
                  ) : null}
                </div>

                <div>
                  <label htmlFor="important-date-end" className="mb-1 block text-sm font-medium text-slate-800">
                    End
                  </label>
                  <input
                    id="important-date-end"
                    type="date"
                    className={cn("w-full border border-slate-300 px-3 py-2 text-sm", form.formState.errors.end && "border-red-500")}
                    {...form.register("end")}
                  />
                  {form.formState.errors.end ? (
                    <p className="mt-1 text-xs text-red-600">{form.formState.errors.end.message}</p>
                  ) : null}
                </div>
              </div>

              <label className="inline-flex items-center gap-2 text-sm text-slate-800">
                <input type="checkbox" className="h-4 w-4" {...form.register("doNotBook")} />
                Do Not Book
              </label>
            </form>

            <div className="border-t border-slate-200 px-4 py-3">
              <div className="flex justify-end gap-2">
                <button type="button" onClick={closeModal} className="border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50">
                  Cancel
                </button>
                <button
                  type="submit"
                  form="important-date-form"
                  disabled={createMutation.isPending || editMutation.isPending}
                  className="inline-flex items-center gap-2 border border-slate-900 bg-slate-900 px-4 py-2 text-sm text-white disabled:opacity-60"
                >
                  {createMutation.isPending || editMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}
